import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import './localOpsActivationProofPage.css';

const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

const getBackendOrigin = () =>
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  'http://localhost:4000';

const safeText = (value, fallback = '') => {
  const clean = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean || fallback;
};

const mediaUrl = (media) => safeText(media?.url || media?.secure_url || '');

const isPublicMedia = (media) =>
  media &&
  media.publicApproved !== false &&
  mediaUrl(media);

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return number.toLocaleString('en-US');
};

const formatTag = (value) => safeText(value).replace(/_/g, ' ');

const formatDate = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const collectActivationProofMediaGroups = (proof = {}) => {
  const snapshot = proof || {};
  const online = [];
  const flyer = [];
  const push = (collection, item, caption) => {
    if (!isPublicMedia(item)) return;
    collection.push({
      url: mediaUrl(item),
      type: item.type === 'video' ? 'video' : 'image',
      thumbnailUrl: safeText(item.thumbnailUrl),
      caption,
    });
  };

  (snapshot.onlineOutreachEntries || [])
    .filter((entry) => entry?.adminHidden !== true)
    .forEach((entry, index) => {
      push(online, entry.screenshotMedia, `Online outreach ${index + 1}`);
    });

  (snapshot.flyerPlacementEntries || [])
    .filter((entry) => entry?.adminHidden !== true)
    .forEach((entry, index) => {
      push(flyer, entry.storefrontPhotoMedia, `Storefront ${index + 1}`);
      (entry.placementPhotoMedia || []).forEach((item, mediaIndex) => {
        push(flyer, item, `Placement ${index + 1}.${mediaIndex + 1}`);
      });
    });

  return {
    online,
    flyer,
    all: [...online, ...flyer],
  };
};

export const collectActivationProofMedia = (proof = {}) =>
  collectActivationProofMediaGroups(proof).all;

export const buildActivationProofShareUrl = (proof, routeProofId) => {
  const id = proof?.publicId || routeProofId || '';
  if (!id || typeof window === 'undefined') return '';
  return `${window.location.origin}/share/localops/activation-proof/${id}`;
};

const getStatsRows = (proof = {}) => {
  const stats = (proof || {}).stats || {};
  return [
    ['Landing views', stats.landingViews],
    ['10s engaged', stats.engaged10s],
    ['60s engaged', stats.engaged60s],
    ['CTA clicks', stats.ctaClicks],
    ['Tasks started', stats.tasksStarted],
    ['Tasks posted', stats.tasksPosted],
    ['Tasks completed', stats.tasksCompleted],
    ['Activation bounties', stats.activationBountiesApproved],
  ];
};

const getFallbackCtaUrl = (proof, intent) => {
  const regionSlug = safeText(proof?.regionSlug, 'camden');
  const slug = safeText(proof?.campaignSlug);
  const url = new URL(`/${regionSlug}`, window.location.origin);
  if (slug) url.searchParams.set('src', slug);
  if (intent) url.searchParams.set('intent', intent);
  return url.toString();
};

const ProofMedia = ({ item, className = '' }) => {
  if (item.type === 'video') {
    return (
      <video
        className={className}
        src={item.url}
        controls
        muted
        playsInline
      />
    );
  }
  return (
    <img
      className={className}
      src={item.thumbnailUrl || item.url}
      alt={item.caption || ''}
      loading="lazy"
    />
  );
};

export default function LocalOpsActivationProofPage() {
  const { proofId } = useParams();
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copyState, setCopyState] = useState('');

  useEffect(() => {
    const previousBodyBg = document.body.style.background;
    const previousHtmlBg = document.documentElement.style.background;
    document.body.style.background = '#050912';
    document.documentElement.style.background = '#050912';
    return () => {
      document.body.style.background = previousBodyBg;
      document.documentElement.style.background = previousHtmlBg;
    };
  }, []);

  useEffect(() => {
    if (!proofId) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetch(
      `${getBackendOrigin()}/share/resolve/localops/activation-proof/${proofId}`,
      {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      },
    )
      .then((response) => {
        if (!response.ok) throw new Error('not-found');
        return response.json();
      })
      .then((json) => {
        if (cancelled) return;
        setProof(json?.data || null);
        setNotFound(!json?.data);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [proofId]);

  const mediaGroups = useMemo(
    () => collectActivationProofMediaGroups(proof),
    [proof],
  );
  const media = mediaGroups.all;
  const onlineMedia = mediaGroups.online;
  const flyerMedia = mediaGroups.flyer;
  const heroMedia = media[0] || null;
  const shareUrl = buildActivationProofShareUrl(proof, proofId);
  const statsRows = getStatsRows(proof);
  const progress = proof?.progress || {};
  const postTaskUrl =
    proof?.ctaLinks?.postTaskUrl || getFallbackCtaUrl(proof, 'requester');
  const helperUrl =
    proof?.ctaLinks?.becomeHelperUrl || getFallbackCtaUrl(proof, 'helper');

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard?.writeText?.(shareUrl);
      setCopyState('Copied');
    } catch (err) {
      setCopyState('Copy unavailable');
    }
  };

  const shareProof = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: proof?.campaignTitle || 'LocalOps activation proof',
          text: proof?.generatedSummary || 'JG LocalOps activation proof',
          url: shareUrl,
        });
        setCopyState('Shared');
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    await copyShareUrl();
  };

  if (loading) {
    return (
      <main className="activation-proof-page">
        <div className="activation-proof-page__state">Loading activation proof...</div>
      </main>
    );
  }

  if (notFound || !proof) {
    return (
      <main className="activation-proof-page">
        <div className="activation-proof-page__state">
          Activation proof unavailable.
        </div>
      </main>
    );
  }

  return (
    <main className="activation-proof-page">
      <section
        className={`activation-proof-hero${heroMedia ? ' has-media' : ''}`}
        style={
          heroMedia && heroMedia.type !== 'video'
            ? { backgroundImage: `url("${heroMedia.url}")` }
            : undefined
        }
      >
        <div className="activation-proof-hero__shade" />
        <div className="activation-proof-hero__content">
          <JGLocalOpsMark
            className="activation-proof-hero__mark"
            label="JG LocalOps"
          />
          <span className="activation-proof-hero__kicker">
            Verified activation proof
          </span>
          <h1>{proof.campaignTitle}</h1>
          <p>{proof.generatedSummary}</p>
          <p className="activation-proof-hero__proof-line">
            We do not just say we promoted it. We show the proof.
          </p>
          <div className="activation-proof-hero__meta">
            <span>{safeText(proof.regionLabel, 'Local market')}</span>
            <span>Approved proof</span>
            {formatDate(proof.approvedAt) ? (
              <span>Approved {formatDate(proof.approvedAt)}</span>
            ) : null}
            <span>{proof.campaignSlug}</span>
          </div>
          <div className="activation-proof-hero__actions">
            <a href={postTaskUrl} className="activation-proof-page-button primary">
              <OpenInNewOutlinedIcon fontSize="small" />
              Post a LocalOps Task
            </a>
            <a href={helperUrl} className="activation-proof-page-button secondary">
              <OpenInNewOutlinedIcon fontSize="small" />
              Become a LocalOps Helper
            </a>
            <button
              type="button"
              className="activation-proof-page-icon"
              onClick={shareProof}
              title="Share proof"
            >
              <ShareOutlinedIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="activation-proof-page-icon"
              onClick={copyShareUrl}
              title="Copy proof link"
            >
              <ContentCopyOutlinedIcon fontSize="small" />
            </button>
          </div>
          {copyState ? (
            <span className="activation-proof-copy-state">{copyState}</span>
          ) : null}
        </div>
        {heroMedia && heroMedia.type === 'video' ? (
          <ProofMedia
            item={heroMedia}
            className="activation-proof-hero__video"
          />
        ) : null}
      </section>

      <section className="activation-proof-band">
        <div className="activation-proof-results">
          <div>
            <strong>{formatNumber(progress.onlineOutreachCount)}</strong>
            <span>online posts</span>
          </div>
          <div>
            <strong>{formatNumber(progress.successfulPlacementCount)}</strong>
            <span>successful placements</span>
          </div>
          <div>
            <strong>{formatNumber(media.length)}</strong>
            <span>approved media</span>
          </div>
          <div>
            <strong>
              {formatNumber(proof.activationBountyClaims?.length || 0)}
            </strong>
            <span>approved leads</span>
          </div>
        </div>
      </section>

      <section className="activation-proof-content">
        <div className="activation-proof-section-title">
          <h2>Live attribution</h2>
          {!proof.stats ? <p>Stats unavailable.</p> : null}
        </div>
        <div className="activation-proof-stat-grid">
          {statsRows.map(([label, value]) => (
            <div className="activation-proof-stat" key={label}>
              <strong>{formatNumber(value)}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="activation-proof-content">
        <div className="activation-proof-section-title">
          <h2>Approved field media</h2>
          <p>{media.length ? `${media.length} public assets` : 'No public media'}</p>
        </div>
        {media.length ? (
          <div className="activation-proof-collage">
            {media.slice(0, 24).map((item, index) => (
              <figure key={`${item.url}-${index}`}>
                <ProofMedia item={item} />
                <figcaption>{item.caption}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <div className="activation-proof-empty-media">
            No media has been approved for public display.
          </div>
        )}
      </section>

      <section className="activation-proof-content">
        <div className="activation-proof-section-title">
          <h2>Online outreach screenshots</h2>
          <p>
            {onlineMedia.length
              ? `${onlineMedia.length} approved screenshots`
              : 'No public online screenshots'}
          </p>
        </div>
        {onlineMedia.length ? (
          <div className="activation-proof-proof-grid">
            {onlineMedia.map((item, index) => (
              <figure key={`${item.url}-${index}`}>
                <ProofMedia item={item} />
                <figcaption>{item.caption}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <div className="activation-proof-empty-media">
            No online outreach screenshots have been approved for public display.
          </div>
        )}
      </section>

      <section className="activation-proof-content">
        <div className="activation-proof-section-title">
          <h2>In-person flyer proof</h2>
          <p>
            {flyerMedia.length
              ? `${flyerMedia.length} approved field assets`
              : 'No public flyer media'}
          </p>
        </div>
        {flyerMedia.length ? (
          <div className="activation-proof-proof-grid">
            {flyerMedia.map((item, index) => (
              <figure key={`${item.url}-${index}`}>
                <ProofMedia item={item} />
                <figcaption>{item.caption}</figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <div className="activation-proof-empty-media">
            No flyer placement media has been approved for public display.
          </div>
        )}
      </section>

      <section className="activation-proof-content split">
        <div>
          <div className="activation-proof-section-title">
            <h2>Outreach notes</h2>
            <p>{proof.leadNotes?.length || 0} public notes</p>
          </div>
          <div className="activation-proof-note-list">
            {(proof.leadNotes || []).map((note, index) => (
              <article key={note._id || index}>
                <p>{note.text}</p>
                <div>
                  {(note.tags || []).map((tag) => (
                    <span key={tag}>{formatTag(tag)}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
        <div>
          <div className="activation-proof-section-title">
            <h2>Activation outcomes</h2>
            <p>{proof.activationBountyClaims?.length || 0} approved claims</p>
          </div>
          <div className="activation-proof-claim-list">
            {(proof.activationBountyClaims || []).map((claim, index) => (
              <article key={claim._id || index}>
                <CheckCircleOutlineIcon fontSize="small" />
                <div>
                  <strong>{claim.contactedSource || 'Approved lead'}</strong>
                  <p>{claim.note}</p>
                  {claim.taskUrl ? (
                    <a href={claim.taskUrl}>Task</a>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
